/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { PAGE_SIZE } from 'Component/ProductList/ProductList.config';
import {
    mapDispatchToProps as sourceMapDispatchToProps,
    mapStateToProps as sourceMapStateToProps,
    ProductListContainer as SourceProductListContainer
} from 'SourceComponent/ProductList/ProductList.container';
import { appendLastLocalPage, updatePagesNumberToShow } from 'Store/ProductList/ProductList.action';

/** @namespace Scandipwa/Component/ProductList/Container/mapStateToProps */
export const mapStateToProps = (state) => ({
    ...sourceMapStateToProps(state),
    isSignedIn: state.MyAccountReducer.isSignedIn,
    isPageLoading: state.ProductListReducer.isPageLoading,
    totalPagesOnServer: state.ProductListReducer.totalPagesOnServer,
    pagesNumberToShow: state.ProductListReducer.pagesNumberToShow
});

/** @namespace Scandipwa/Component/ProductList/Container/mapDispatchToProps */
export const mapDispatchToProps = (dispatch) => ({
    ...sourceMapDispatchToProps(dispatch),
    appendLastLocalPage: (currentPage) => dispatch(appendLastLocalPage(currentPage)),
    updatePagesNumberToShow: (pagesNumberToShow) => dispatch(updatePagesNumberToShow(pagesNumberToShow))
});

/**
 * Category sort
 * @class CategorySort
 * @namespace Scandipwa/Component/ProductList/Container */
export class ProductListContainer extends SourceProductListContainer {
    static defaultProps = {
        ...SourceProductListContainer.defaultProps,
        // eslint-disable-next-line no-magic-numbers
        pageSize: PAGE_SIZE
    };

    state = {
        ...this.state,
        hasSignedIn: false
    };

    containerProps() {
        const {
            isPageLoading,
            pageSize,
            totalItems,
            areComponentsLoading,
            isInfoLoading,
            areMenuFiltersLoading,
            pagesNumberToShow,
            updatePagesNumberToShow,
            sort
        } = this.props;

        return {
            ...super.containerProps(),
            isPageLoading,
            pageSize,
            totalItems,
            areComponentsLoading,
            isInfoLoading,
            areMenuFiltersLoading,
            pagesNumberToShow,
            updatePagesNumberToShow,
            sort
        };
    }

    componentDidUpdate(prevProps) {
        const {
            sort,
            search,
            filter,
            pages,
            isSignedIn
        } = this.props;

        const {
            sort: prevSort,
            search: prevSearch,
            filter: prevFilter,
            pages: prevPages
        } = prevProps;

        const { pagesCount, hasSignedIn } = this.state;

        if (isSignedIn !== hasSignedIn) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                hasSignedIn: isSignedIn
            });

            this.requestPage();
        }

        const pagesLength = Object.keys(pages).length;

        if (pagesCount !== pagesLength) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ pagesCount: pagesLength }, () => {
                if (pagesLength === 1) {
                    this.requestPage(2, true);
                }
            });
        } else if (pagesLength === 1) {
            if (Object.values(pages)[0][0]?.id !== Object.values(prevPages)[0][0]?.id) {
                this.requestPage(2, true);
            }
        }

        if (search !== prevSearch
            || JSON.stringify(sort) !== JSON.stringify(prevSort)
            || JSON.stringify(filter) !== JSON.stringify(prevFilter)
        ) {
            this.setState({ pagesCount: 0 }, () => this.requestPage(this._getPageFromUrl()));
        }
    }

    /**
     * Returns if isInfinite loader status
     *
     * @returns {*}
     * @private
     */
    _getIsInfiniteLoaderEnabled() {
        const { isInfiniteLoaderEnabled } = this.props;

        return isInfiniteLoaderEnabled;
    }

    loadPage(next = true) {
        const { pagesCount } = this.state;
        const {
            isPageLoading,
            appendLastLocalPage,
            totalPagesOnServer
        } = this.props;

        const {
            minPage,
            maxPage,
            totalPages,
            loadedPagesCount
        } = this._getPagesBounds();
        const isUpdatable = totalPages > 0 && pagesCount === loadedPagesCount;
        const shouldUpdateList = next ? maxPage < totalPages : minPage > 1;

        if (isUpdatable && shouldUpdateList && !isPageLoading) {
            this.setState({ pagesCount: pagesCount + 1 });

            if (loadedPagesCount + 1 >= totalPagesOnServer && totalPagesOnServer < totalPages) {
                appendLastLocalPage(loadedPagesCount + 1);
            } else {
                this.requestPage(next ? maxPage + 1 : minPage - 1, true);
            }
        }
    }

    updatePage() {
        // Do nothing
    }

    requestPage = (currentPage = 1, isNext = false) => {
        const {
            sort,
            search,
            filter,
            pageSize,
            requestProductList,
            requestProductListInfo,
            noAttributes,
            noVariants
        } = this.props;

        /**
         * In case the wrong category was passed down to the product list,
         * prevent it from being requested.
         */
        if (filter.categoryIds === -1) {
            return;
        }

        /**
         * Do not request page if there are no filters
         */
        if (!search && !this.isEmptyFilter()) {
            return;
        }

        // TODO: product list requests filters alongside the page
        // TODO: sometimes product list is requested more then once
        // TODO: the product list should not request itself, when coming from PDP

        const options = {
            isNext,
            noAttributes,
            noVariants,
            args: {
                sort,
                filter,
                search,
                pageSize,
                currentPage
            }
        };

        const infoOptions = {
            args: {
                filter,
                search
            },
            minMaxRequested: true
        };

        requestProductList(options);

        if (!isNext) {
            requestProductListInfo(infoOptions);
        }
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ProductListContainer));
